import {
  defineStore,
  storeToRefs,
} from 'pinia';
import { ElNotification } from 'element-plus';
import api from '@/services/api';
import request_model from '../models/request_model';
import request_filters_model from '../models/request_filters_model';
import request_producto_model from '../models/request_producto_model';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';

import initial_delivery_model from '@/shared/models/delivery_model.js';
import { getParamsUrl } from '../request_utils';
import { useAuthStore } from '../../auth/auth_store';
import elemento_grupo_service from '@/services/elemento_grupo_service';
import { Modules } from '@/enum/modules';
import { generarElemento } from '@/shared/generators/genericGenerator';
import { ElementoBase } from '@/enum/elemento_base';
import { Proceso } from '@/enum/proceso';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { Role } from '@/enum/roles_enum.js';
import { cantidadesIguales } from '@/shared/utils/cantidad_unidad_venta.js';

export const useRequestStore = defineStore('requestStore', {
  state: () => ({
    solicitud: JSON.parse(JSON.stringify(request_model)),
    filters: { ...request_filters_model },
    validarSolicitudCampos: {
      ClienteDescripcion: true,
      Cliente: true,
      Referencia: true,
      MedidaEje: true,
      MedidaDesarrollo: true,
      AnchoMaterial: true,
      CantidadEtiquetas: true,
      MargenSuperior: true,
      FamiliaMaterial: true,
      GapEje: true,
      GapDesarrollo: true,
      Forma: true,
      EtiquetasDesarrollo: true,
      EtiquetasEje: true,
      CodigoCliente: true,
      CodigoUnidadVenta: true,
      CodigoMoneda: true,
      SolicitudCantidad: [],
      Solapa: true,
      Sellado: true,
      Base: true,
      AltoTotal: true,
    },
    // Nombres de los campos que fallaron en la última validación, para poder decirle al
    // usuario qué falta en vez de un "revisar formulario" a secas.
    camposSolicitudInvalidos: [],
    solicitudes: [],
    page: 1,
  }),
  getters: {
    validarSumatoriaCantidades: (state) => {
      const cabecera = state.solicitud?.SolicitudCantidad || [];
      const productos = state.solicitud?.SolicitudProducto || [];

      if (productos.length === 0) return cabecera.map(() => true);

      return cabecera.map((qtyCabecera, index) => {
        const totalCabecera = Number(qtyCabecera.Cantidad || 0);

        const sumatoriaDetalle = productos.reduce((acc, prod) => {
          const detalle = prod.SolicitudProductoCantidad || [];
          const fila = detalle.find((x) => x.ItemCantidad === index + 1) ?? detalle[index];
          return acc + Number(fila?.Cantidad || 0);
        }, 0);

        return cantidadesIguales(totalCabecera, sumatoriaDetalle);
      });
    },
    cantidadesValidadas: (state) => {
      return state.validarSumatoriaCantidades.every((isValid) => isValid === true);
    },
  },
  actions: {
    async findAllSolicitudesHTTP(params) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();
      const esEjecutivoDeVentas = usuario.roles.some(
        (rol) => rol.Codigo === Role.EJECUTIVO_DE_VENTAS,
      );

      if (esEjecutivoDeVentas) {
        this.filters.CodigoUsuarioCreacion = usuario.Codigo;
      }

      const result = await api.get(
        `/solicitud?${getParamsUrl({ ...this.filters, page: +this.page, ...params })}`,
      );
      if (result && result.data && result.data.length > 0) {
        this.solicitudes.push(...result.data);
        this.setPage(this.page + 1);
        return true;
      }
      return false;
    },
    async firstRequest() {
      this.setPage(1);
      this.emptyList();
      this.resetFilter();
      await this.findAllSolicitudesHTTP();
    },
    async createSolicitudHTTP() {
      const response = await api.post('/solicitud', this.solicitud);
      if (response.status === 200 || response.status === 201) {
        return response;
      }
    },
    async createSolicitudFromEspecificacionesTecnicasHTTP(EspecificacionesTecnicas) {
      const response = await api.post('/solicitud/from-technical-specification', {
        EspecificacionesTecnicas,
      });
      if ((response && response.status === 200) || response.status === 201) {
        return response.data;
      }
      throw new Error('No se pudo crear la solicitud.');
    },
    async updateSolicitudHTTP() {
      const codigo = this.solicitud.Codigo;
      const response = await api.patch(`/solicitud/${codigo}`, this.solicitud);
      if (response.status === 200) {
        return response;
      }
    },
    async cloneSolicitudHTTP(codigoSolicitud, payload) {
      const response = await api.post(`/solicitud/clone/${codigoSolicitud}`, payload);
      if (response.status === 200 || response.status === 201) {
        // this.resetSolicitud()
        // await this.firstRequest()
        return {
          solicitud: response.data.Codigo,
          cotizacion: null,
        };
      }
    },
    async cloneSolicitudYCotizacionHTTP(codigoSolicitud, payload) {
      const response = await api.post(`/solicitud/clone-cotizacion/${codigoSolicitud}`, payload);
      if (response.status === 200 || response.status === 201) {
        // this.resetSolicitud()
        // await this.firstRequest()
        return {
          solicitud: response.data.CodigoSolicitud,
          cotizacion: response.data.Codigo,
        };
      }
    },
    // async anularSolicitudHTTP() {
    //   const codigo = this.request.Codigo
    //   this.request.EstadoSolicitud.Codigo = EstadoSolicitud.ANULADA
    //   const response = await api.patch(`/solicitud/${codigo}`, this.request)
    //   if (response.status === 200) {
    //     this.resetSolicitud()
    //     return
    //   }
    // },
    async findSolicitudPorCodigoHTTP(codigo) {
      try {
        const result = await api.get(`/solicitud/${codigo}`);
        if (result && result.data) {
          this.solicitud = result.data;
        }
      } catch (error) {
        console.error('Error en findSolicitudPorCodigoHTTP:', error);
      }
    },
    async getDiferenciasEtHTTP(codigo) {
      const response = await api.get(`/solicitud/${codigo}/diferencias-et`);
      return response.data;
    },
    async actualizarEtHTTP(codigo, { actualizarTodos, camposAActualizar }) {
      const response = await api.patch(`/solicitud/${codigo}/actualizar-et`, {
        actualizarTodos,
        camposAActualizar,
      });
      return response.data;
    },
    addEntrega() {
      this.solicitud.SolicitudEntregas &&
        this.solicitud.SolicitudEntregas.push({
          ...initial_delivery_model,
          CodigoSolicitud: null,
        });
    },
    removeEntrega() {
      const deliveries = this.solicitud.SolicitudEntregas;
      if (deliveries && deliveries.length) {
        this.solicitud.SolicitudEntregas = deliveries.slice(0, deliveries.length - 1);
      }
    },
    updateEntrega({ delivery, index }) {
      if (this.solicitud.SolicitudEntregas && index >= 0) {
        const deliveries = [...this.solicitud.SolicitudEntregas];
        const { Cantidad, Fecha } = delivery;
        deliveries[index] = { ...this.solicitud.SolicitudEntregas[index], Cantidad, Fecha };
        this.solicitud.SolicitudEntregas = deliveries;
      }
    },
    addCantidad() {
      const cantidades = this.solicitud.SolicitudCantidad;
      const nueva = {
        Codigo: null,
        CodigoSolicitud: null,
        ItemCantidad: cantidades.length + 1,
        Cantidad: null,
        PrecioReferencia: null,
      };
      cantidades.push(nueva);
      this.solicitud.SolicitudProducto?.forEach((prod) => {
        if (!prod.SolicitudProductoCantidad) prod.SolicitudProductoCantidad = [];
        prod.SolicitudProductoCantidad.push({ Cantidad: null, ItemCantidad: nueva.ItemCantidad });
      });
    },
    removeCantidad(index) {
      const cantidades = this.solicitud.SolicitudCantidad;
      if (cantidades.length <= 1) {
        ElNotification({
          title: 'Cantidades',
          message: 'Debe ingresar al menos una cantidad a cotizar',
          type: 'warning',
        });
        return;
      }
      cantidades.splice(index, 1);
      cantidades.forEach((c, i) => (c.ItemCantidad = i + 1));
      this.solicitud.SolicitudProducto?.forEach((prod) => {
        prod.SolicitudProductoCantidad?.splice(index, 1);
        prod.SolicitudProductoCantidad?.forEach((c, i) => (c.ItemCantidad = i + 1));
      });
    },
    updateCantidad({ index, field, value }) {
      const cantidad = this.solicitud.SolicitudCantidad[index];
      if (!cantidad) return;
      cantidad[field] = value;
      if (field === 'Cantidad') this.autoCompletarCantidadUnica(index);
    },
    addProducto() {
      if (!this.solicitud.SolicitudProducto) {
        this.solicitud.SolicitudProducto = [];
      }
      const nuevoProducto = { ...request_producto_model };
      const esUnicoProducto = this.solicitud.SolicitudProducto.length === 0;

      nuevoProducto.SolicitudProductoCantidad = (this.solicitud.SolicitudCantidad || []).map(
        (qty) => ({
          Cantidad: esUnicoProducto ? qty.Cantidad : null,
          ItemCantidad: qty.ItemCantidad,
        }),
      );

      this.solicitud.SolicitudProducto.push(nuevoProducto);
    },
    autoCompletarCantidadUnica(indexCabecera) {
      const productos = this.solicitud.SolicitudProducto;
      if (productos && productos.length === 1) {
        const cantidadCabecera = this.solicitud.SolicitudCantidad[indexCabecera].Cantidad;
        if (
          productos[0].SolicitudProductoCantidad &&
          productos[0].SolicitudProductoCantidad[indexCabecera]
        ) {
          productos[0].SolicitudProductoCantidad[indexCabecera].Cantidad = cantidadCabecera;
        }
      }
    },
    updateProducto(data) {
      this.solicitud.SolicitudProducto[data.index] = data.producto;

      if (this.solicitud.SolicitudProducto.length === 1) {
        const cantidadesDetalle = data.producto.SolicitudProductoCantidad || [];

        cantidadesDetalle.forEach((detalleQty, index) => {
          if (this.solicitud.SolicitudCantidad[index] && detalleQty.Cantidad !== null) {
            this.solicitud.SolicitudCantidad[index].Cantidad = detalleQty.Cantidad;
          }
        });
      }
    },
    removeProducto(index) {
      const productos = JSON.parse(JSON.stringify(this.solicitud.SolicitudProducto));
      if (productos && productos.length) {
        productos.splice(index, 1);
        this.solicitud.SolicitudProducto = productos;
      }
    },
    async createFromETHTTP(codigoET, payload) {
      const response = await api.post(
        `/solicitud/from-especificacion-tecnica/${codigoET}`,
        payload,
      );
      return response.data || response;
    },
    resetValidaciones() {
      Object.keys(this.validarSolicitudCampos).forEach((key) => {
        this.validarSolicitudCampos[key] = true;
      });
      this.camposSolicitudInvalidos = [];
    },

    validarCampos() {
      const elementoBaseStore = useElementoBaseStore();
      const { elementoBaseConfig } = storeToRefs(elementoBaseStore);

      const elemento = this.solicitud.SolicitudElemento[0];
      const codigoBase = elemento.ElementoTipo.CodigoElementoBase;
      const permiteGapCero = [ElementoBase.CAJA, ElementoBase.FLEXIBLE].includes(codigoBase);
      this.validarSolicitudCampos = {
        AltoTotal: true,
        AnchoMaterial: true,
        Base: true,
        CantidadEtiquetas: true,
        ClienteDescripcion: !!this.solicitud.Cliente.Descripcion,
        CodigoCliente: !!this.solicitud.CodigoCliente,
        CodigoMoneda: !!this.solicitud.Moneda?.Codigo,
        CodigoUnidadVenta: !!this.solicitud.CodigoUnidadVenta,
        EtiquetasDesarrollo: true,
        EtiquetasEje: true,
        FamiliaMaterial: true,
        Forma: true,
        GapDesarrollo: true,
        GapEje: true,
        MargenSuperior: true,
        MedidaDesarrollo: true,
        MedidaEje: true,
        Referencia: !!this.solicitud.Referencia,
        Sellado: true,
        Solapa: true,
        SolicitudCantidad: [],
      };

      // COTI-507: extrusion no usa los campos del elemento base (suaje, medidas, familia
      // de material), asi que se saltean todas esas validaciones y en su lugar se validan
      // los datos del film resultante, que son los que el cotizador necesita.
      if (codigoBase === ElementoBase.EXTRUSION) {
        this.validarSolicitudCampos.FamiliaMaterial = true;
        this.validarCamposExtrusion(elemento);
      } else {
        if (elementoBaseConfig.value.campos.mostrarMedidaEje) {
          this.validarSolicitudCampos.MedidaEje = !!elemento.MedidaEje;
        }
        if (elementoBaseConfig.value.campos.mostrarMedidaDesarrollo) {
          this.validarSolicitudCampos.MedidaDesarrollo = !!elemento.MedidaDesarrollo;
        }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasEje) {
          this.validarSolicitudCampos.EtiquetasEje = !!elemento.EtiquetasEje;
        }
        if (elementoBaseConfig.value.campos.mostrarEtiquetasDesarrollo) {
          this.validarSolicitudCampos.EtiquetasDesarrollo = !!elemento.EtiquetasDesarrollo;
        }
        // COTI-422: Se definio que el sistema no valide gaps
        // if(elementoBaseConfig.value.campos.mostrarGapEje) {
        //   this.validarSolicitudCampos.GapEje = !!elemento.GapEje
        // }
        // if(elementoBaseConfig.value.campos.mostrarGapDesarrollo) {
        //   this.validarSolicitudCampos.GapDesarrollo = !!elemento.GapDesarrollo
        // }

        if (
          ![
            ElementoBase.TERMO_ENCOGIBLE,
            ElementoBase.CAJA,
            ElementoBase.CONSUMIBLE,
            ElementoBase.FLEXIBLE,
            ElementoBase.BOLSAS_FUELLE,
            ElementoBase.BOLSAS_BOX,
            // COTI-502: en offset el campo de familia de material no se muestra, así que
            // exigirlo dejaba el formulario inválido sin nada que el usuario pudiera corregir.
            ElementoBase.OFFSET,
          ].includes(codigoBase)
        ) {
          this.validarSolicitudCampos.FamiliaMaterial = !!elemento.MateriaPrimaFamilia?.Codigo;
        } else if ([ElementoBase.BOLSAS_BOX].includes(codigoBase)) {
          this.validarSolicitudCampos.Sellado = !!elemento.Sellado;
          this.validarSolicitudCampos.Base = !!elemento.Base;
        } else if ([ElementoBase.BOLSAS_FUELLE].includes(codigoBase)) {
          this.validarSolicitudCampos.Base = !!elemento.Base;
        } else {
          this.validarSolicitudCampos.FamiliaMaterial = true;
        }
      }

      return this.verificarValidacionPropiedades();
    },
    /**
     * Datos del film resultante que el cotizador necesita cuando el elemento se resuelve
     * por dimensionamiento continuo. Sin el gramaje —cargado directo o derivado de
     * Densidad x Espesor, según calculo_material_extrusion— no puede derivar el largo del
     * pedido y falla con "desarrolloMm debe ser > 0".
     *
     * El ancho del elemento no se valida como campo propio: en extrusión no se carga a
     * mano, sale de la sumatoria de los anchos de las bobinas del proceso. Lo que se exige
     * son las bobinas. Mismo criterio que en cotización (prices_store).
     */
    validarCamposExtrusion(elemento) {
      const { calculoMaterialExtrusion } = useConfiguracionStore();
      const esCalculoPorGramaje = calculoMaterialExtrusion === 'gramaje';

      const procesos = elemento.SolicitudElementoProceso || [];
      const procesosExtrusion = procesos.filter(
        (p) => (p.Proceso?.Codigo ?? p.CodigoProceso) === Proceso.EXTRUSION,
      );
      // Todavía no se agregó el proceso de extrusión: no hay film del que hablar.
      if (procesosExtrusion.length === 0) return;

      // Mismo criterio que esSoloExtrusion en el cotizador: si la extrusión convive con
      // otros procesos, el elemento se dimensiona por pliego físico y el gramaje del film
      // no entra en el cálculo, así que no hay por qué exigirlo.
      if (procesos.length !== procesosExtrusion.length) return;

      const cargado = (valor) =>
        valor !== null && valor !== undefined && valor !== '' && Number(valor) > 0;
      const todos = (predicado) => procesosExtrusion.every(predicado);

      this.validarSolicitudCampos.MaterialResultante = todos((p) =>
        cargado(p.CodigoMaterialResultante ?? p.MaterialResultante?.Codigo),
      );
      // De acá sale el ancho del material, que el cotizador exige > 0.
      this.validarSolicitudCampos.BobinasExtrusion = todos((p) =>
        (p.ElementoProcesoBobinas || []).some((bobina) => cargado(bobina?.Ancho)),
      );

      if (esCalculoPorGramaje) {
        this.validarSolicitudCampos.GramajeResultante = todos((p) => cargado(p.GramajeResultante));
      } else {
        this.validarSolicitudCampos.EspesorResultante = todos((p) => cargado(p.EspesorResultante));
        this.validarSolicitudCampos.DensidadResultante = todos((p) =>
          cargado(p.DensidadResultante),
        );
      }
    },
    validarCamposCotizacion() {
      const elemento = this.solicitud.SolicitudElemento[0];

      // COTI-507: en extrusion no hay pliego, las medidas quedan en 0 y no se validan.
      if (elemento?.ElementoTipo?.CodigoElementoBase === ElementoBase.EXTRUSION) {
        this.validarSolicitudCampos.MedidaEje = true;
        this.validarSolicitudCampos.MedidaDesarrollo = true;
        this.validarCamposExtrusion(elemento);
        return this.verificarValidacionPropiedades();
      }

      this.validarSolicitudCampos.MedidaEje = !!elemento.MedidaEje;
      this.validarSolicitudCampos.MedidaDesarrollo = !!elemento.MedidaDesarrollo;
      // Para validar la forma debe estar visible y en solicitudes mo esta visible
      // if (!
      //   [ElementoBase.TERMO_ENCOGIBLE, ElementoBase.FLEXIBLE, ElementoBase.CAJA]
      //   .includes(this.solicitud.SolicitudElemento[0].ElementoTipo.CodigoElementoBase)) {
      //   this.validarSolicitudCampos.Forma = !!this.solicitud.SolicitudElemento[0].Forma.Codigo
      // }
      return this.verificarValidacionPropiedades();
    },
    validarCantidades() {
      return this.solicitud.SolicitudCantidad.some((sc) => sc.Cantidad > 0);
    },
    verificarValidacionPropiedades() {
      this.camposSolicitudInvalidos = Object.keys(this.validarSolicitudCampos).filter(
        (property) => !this.validarSolicitudCampos[property],
      );

      if (this.camposSolicitudInvalidos.length > 0) {
        console.debug(
          `Las propiedades no válidas son: ${this.camposSolicitudInvalidos.join(', ')}`,
        );
        return false;
      }
      return true;
    },
    setPage(page) {
      this.page = page;
    },
    emptyList() {
      this.solicitudes = [];
    },
    setFilters(newFilters) {
      this.filters = { ...newFilters };
    },
    resetFilter() {
      this.filters = { ...request_filters_model };
    },
    resetSolicitud() {
      this.solicitud = JSON.parse(JSON.stringify(request_model));
    },
    async setDefaults(codigo) {
      const elementosDefaults = await elemento_grupo_service.getGrupoDefaults(
        codigo,
        Modules.SOLICITUD,
      );

      elementosDefaults.forEach((elementoDefault, index) => {
        const elementoGenerated = generarElemento(Modules.SOLICITUD, elementoDefault, index);
        this.solicitud.SolicitudElemento[index].SolicitudElementoProceso =
          elementoGenerated.SolicitudElementoProceso;
      });
    },
    async newRequestToPriceHTTP() {
      const response = await api.post('/cotizacion', {
        ...this.solicitud,
        CodigoCliente: this.solicitud.Cliente.Codigo,
      });
      if (response.status === 200 || response.status === 201) {
        this.resetSolicitud();
        return;
      }
    },
    setValidacionSolicitudCantidad(value) {
      this.validarSolicitudCampos.SolicitudCantidad = value;
    },
  },
});
